/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import AppContainer from '@/components/layout/container';
import { Api } from '@/api';
import AppTitle from '@/components/common/title';
import s from './styles.module.scss';
import OurAppList from '@/components/our-apps/list';
import { IPromotionalApp } from '@/infrastructure/interfaces/i-promotional-app';
import SeoHead from '@/components/common/seo-head';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { useRouter } from 'next/router';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { addDomainForPromotionalAppImages } from '@/utils/helpers/add-domain-for-promotion-apps';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;
const OUR_APPS_DOMAIN = process.env.OUR_APPS_DOMAIN || '';

interface IOurAppsPageProps {
  promotionalApps: IPromotionalApp[];
}

const OurAppsPage: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<IOurAppsPageProps>(data);
  const { promotionalApps } = decrypted;

  const router = useRouter();
  const { locale } = router;

  const { t: tLinks } = useTranslation('links');
  const { t: tMeta } = useTranslation('meta');

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('ourApps'),
      path: routes.ourApps,
    },
  ];

  const shema = baseMarkupSchema([
    webPageMarkupSchema(
      routes.ourApps,
      tMeta('ourAppsPageTitle'),
      tMeta('ourAppsPageDescription')
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);
  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}` + routes.ourApps;

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <SeoHead
        title={tMeta('ourAppsPageTitle')}
        description={tMeta('ourAppsPageDescription')}
        canonicalURL={canonicalURL}
        schema={shema}
      />
      <main>
        <AppContainer>
          <AppTitle className={s['title']} title={tLinks('ourApps')} main />
          <OurAppList items={promotionalApps} />
        </AppContainer>
      </main>
    </>
  );
};

OurAppsPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale = '' } = ctx;

    const { data: promotionalApps } = await api.getPromotionalApps();
    promotionalApps.map((app: IPromotionalApp) => {
      addDomainForPromotionalAppImages(app, OUR_APPS_DOMAIN);
    });

    const dataToEncrypt = {
      promotionalApps,
    };
    const { encryptPageProps } =
      await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default OurAppsPage;
